import React, {useEffect, useState} from "react";
import {
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, DrawerCloseButton,
  Text, VStack, HStack, IconButton, Textarea, Input, Button, Checkbox, useToast, Flex
} from "@chakra-ui/react";
import {AiFillStar, AiOutlineStar} from "react-icons/ai";
import {MdDelete} from "react-icons/md";
import AppSpinner from "../AppSpinner.tsx";
import iso6391 from "iso-639-1";
import RadioCards from "./RadioCards.tsx";
import { useProjectsStore } from "../../store/useProjectsStore.ts";
import {IFeedbackForm} from "../../models/interfaces/feedbackForm.interface.ts";
import {IFeedbackFormExample} from "../../models/interfaces/feedbackFormExample.interface.ts";

interface FeedbackDrawerProps {
  projectId: string;
  targetLanguages: string[];
  maxRowNumber: number;
  isOpen: boolean;
  onClose: () => void;
}

const categories: string[] = ["Overall Score", "Slang Score", "Line Break Score", "Subtitles Separation Score"];

const FeedbackDrawer: React.FC<FeedbackDrawerProps> = (
  {
    isOpen,
    onClose,
    maxRowNumber,
    projectId,
    targetLanguages
  }: FeedbackDrawerProps) => {
  const [ratings, setRatings] = useState<{ [key: string]: number }>({
    "Overall Score": 0,
    "Slang Score": 0,
    "Line Break Score": 0,
    "Subtitles Separation Score": 0,
  });
  const {sendProjectFeedback} = useProjectsStore();
  const toast = useToast()
  const [targetLang, setTargetLang] = useState(targetLanguages[0]); // State for target language
  const [description, setDescription] = useState(""); // State for description
  const [examples, setExamples] = useState<IFeedbackFormExample[]>([]); // List of examples
  const [rowNumber, setRowNumber] = useState<number | "">(""); // Input for row number
  const [exampleText, setExampleText] = useState(""); // Input for example text
  const [canContact, setCanContact] = useState(false); // Checkbox state
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTargetLang(targetLanguages[0])
    }
  }, [isOpen, setTargetLang, targetLanguages]);

  const handleRating = (category: string, score: number) => {
    setRatings((prev) => ({...prev, [category]: score}));
  };

  const addExample = () => {
    if (rowNumber === "" || rowNumber > maxRowNumber || exampleText.trim() === "") return;

    setExamples([...examples, {rowIndex: Number(rowNumber), description: exampleText}]);
    setRowNumber(""); // Reset inputs
    setExampleText("");
  };

  const removeExample = (index: number) => {
    setExamples(examples.filter((_, i) => i !== index));
  };

  const submitFeedback = async () => {
    try {
      setIsSubmitting(true);
      const feedbackData: IFeedbackForm = {
        description: description,
        score: ratings[categories[0]],
        slangScore: ratings[categories[1]],
        lineBreakScore: ratings[categories[2]],
        subtitlesSeparationScore: ratings[categories[3]],
        examples: examples,
        canContactAuthor: canContact,
      };
      const targetLangCode = iso6391.getCode(targetLang)
      await sendProjectFeedback(projectId, targetLangCode, feedbackData);
      onClose(); // Close drawer after submission
      toast({
        position: "bottom-right",
        title: "Thanks for your feedback",
        description: "",
        status: "info",
        duration: 3000,
        isClosable: false,
      });
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    //   reset feedback
    setRatings({
      "Overall Score": 0,
      "Slang Score": 0,
      "Line Break Score": 0,
      "Subtitles Separation Score": 0,
    });
    setDescription("");
    setExamples([]);
    setRowNumber("");
    setExampleText("");
    setCanContact(false);

  }, [isOpen]);

  const canSubmit = targetLang && ratings["Overall Score"] > 0;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay/>
      <DrawerContent bg="gray.800" borderLeft="1px solid gray.700">
        <DrawerCloseButton color="white"/>
        <DrawerHeader color="white" borderBottom="1px solid gray.700">
          Provide feedback
        </DrawerHeader>
        <DrawerBody>
          {targetLanguages.length > 1 && (
            <Flex className="mb-2">
              <RadioCards
                options={targetLanguages}
                setTargetLang={setTargetLang}
              />
            </Flex>
          )}
          <VStack align="start" spacing={0} width="100%">
            {/* Rating Categories */}
            {categories.map((category) => (
              <VStack align="start" key={category} width="100%">
                <Text color="white" fontSize="16px" fontWeight="bold">{category}</Text>
                <HStack>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <IconButton
                      key={star}
                      aria-label={`Rate ${star}`}
                      icon={star <= ratings[category]
                        ? <AiFillStar className="text-brandPurple-500" size={24}/>
                        : <AiOutlineStar className="text-gray-500 hover:text-brandPurple-500" size={24}/>}
                      variant="ghost"
                      _hover={{bg: ""}}
                      _active={{bg: ""}}
                      onClick={() => handleRating(category, star)}
                    />
                  ))}
                </HStack>
              </VStack>
            ))}

            {/* Description Box */}
            <VStack align="start" width="100%" spacing={2} mt={4}>
              <Text color="white" fontSize="16px" fontWeight="bold">Description</Text>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Write your feedback here..."
                bg="gray.700"
                color="white"
                borderColor="gray.600"
                _placeholder={{color: "gray.400"}}
                _hover={{borderColor: "brandPurple.400"}}
                _focus={{borderColor: "brandPurple.500", boxShadow: "0 0 0 1px var(--chakra-colors-brandPurple-500)"}}
                resize="none"
                rows={4}
              />
            </VStack>

            {/* Examples Section */}
            <VStack align="start" width="100%" spacing={2} mt={4}>
              <Text color="white" fontSize="16px" fontWeight="bold">Examples</Text>

              {/* Example Input Row */}
              <HStack width="100%">
                <Input
                  type="number"
                  value={rowNumber}
                  onChange={(e) => setRowNumber(e.target.value ? Math.min(Number(e.target.value), maxRowNumber) : "")}
                  placeholder={`# Row`}
                  bg="gray.700"
                  color="white"
                  borderColor="gray.600"
                  _placeholder={{color: "gray.400"}}
                  _hover={{borderColor: "brandPurple.400"}}
                  _focus={{borderColor: "brandPurple.500", boxShadow: "0 0 0 1px var(--chakra-colors-brandPurple-500)"}}
                  width="20%"
                />
                <Input
                  value={exampleText}
                  onChange={(e) => setExampleText(e.target.value)}
                  placeholder="Example description"
                  bg="gray.700"
                  color="white"
                  borderColor="gray.600"
                  _placeholder={{color: "gray.400"}}
                  _hover={{borderColor: "brandPurple.400"}}
                  _focus={{borderColor: "brandPurple.500", boxShadow: "0 0 0 1px var(--chakra-colors-brandPurple-500)"}}
                  flex={1}
                />
                <Button onClick={addExample} bg="#8732F4" color="white" _hover={{bg: "#6b28c8"}}>
                  Add Example
                </Button>
              </HStack>

              {/* List of Added Examples */}
              {examples.length > 0 && (
                <VStack align="start" width="100%" spacing={2} mt={2}>
                  {examples.map((ex, index) => (
                    <HStack key={index} width="100%" bg="gray.700" p={2} rounded="md" justify="space-between">
                      <Text color="white" fontSize="14px"><strong>Row {ex.rowIndex}:</strong> {ex.description}</Text>
                      <IconButton
                        icon={<MdDelete size={18} color="#ad0725"/>}
                        aria-label="Delete example"
                        variant="ghost"
                        onClick={() => removeExample(index)}
                      />
                    </HStack>
                  ))}
                </VStack>
              )}
            </VStack>
          </VStack>
        </DrawerBody>

        {/* Footer */}
        <DrawerFooter borderTop="1px solid gray.700">
          <HStack width="100%" justify="space-between">
            {/* Can Contact Me Checkbox */}
            <Checkbox
              colorScheme="brandPurple"
              color="white"
              isChecked={canContact}
              onChange={(e) => setCanContact(e.target.checked)}
              _hover={{borderColor: "brandPurple.400"}}
            >
              Can contact me
            </Checkbox>

            {/* Submit Button */}
            <Button
              bg="#8732F4"
              color="white"
              minW={"170px"}
              _hover={{bg: "#6b28c8"}}
              onClick={submitFeedback}
              isDisabled={!canSubmit || isSubmitting}
            >
              {isSubmitting ? (
                <AppSpinner size={"md"} color={"white"}/>
              ) : 'Submit Feedback'}
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FeedbackDrawer;